import { useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import styled from 'styled-components';

import { ConversionCreditCampaignBanner } from '@sorare/core/src/components/conversionCredit/ConversionCreditCampaignBanner';
import { LeaguesBlock } from '@sorare/core/src/components/landing/LeaguesBlock';
import { NewOtherSports } from '@sorare/core/src/components/landing/NewOtherSports';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSeoContext } from '@sorare/core/src/contexts/seo';
import useEvents from '@sorare/core/src/lib/events/useEvents';

import useFontFaceObserver from '@sorare/use-font-face-observer';

import { Community } from './Community';
import { Experience } from './Experience';
import { FAQ } from './FAQ';
import { Hero } from './Hero';
import { Prizes } from './Prizes';
import metadataImage from './assets/metadataImage.jpg';

const messages = defineMessages({
  metadatasTitle: {
    id: 'MLBLanding.metadatasTitle',
    defaultMessage:
      'Play fantasy baseball with ownable MLB player cards - Sorare',
  },
  metadatasDescription: {
    id: 'MLBLanding.metadatasDescription',
    defaultMessage:
      "Play Sorare's free fantasy Major League Baseball game. Collect, buy, sell, and compete with ownable digital player cards from all 30 MLB teams to win rewards.",
  },
});

const DarkBackground = styled.div`
  overflow: hidden;
  position: relative;
  background-color: black;
  color: var(--c-static-neutral-100);
`;

export const Landing = () => {
  const { formatMessage } = useIntl();
  const { setPageMetadata } = useSeoContext();
  const { currentUser } = useCurrentUserContext();
  const fontStatus = useFontFaceObserver(
    [{ family: 'DrukWide-Super', weight: 'bold' }],
    { timeout: 1000 }
  );
  const track = useEvents();

  useEffect(
    () =>
      setPageMetadata(formatMessage(messages.metadatasTitle), {
        img: metadataImage,
        description: formatMessage(messages.metadatasDescription),
      }),
    [setPageMetadata, formatMessage]
  );

  useEffect(() => {
    if (!currentUser) {
      track('View Homepage Disconnected', {
        landing_variant: 'MLB',
      });
    }
  }, [currentUser, track]);

  if (fontStatus === 'initial') return null; // allow rendering without the right font after the timeout
  return (
    <DarkBackground>
      <ConversionCreditCampaignBanner />
      <Hero />
      <LeaguesBlock />
      <Experience />
      <Prizes />
      <Community />
      <NewOtherSports />
      <FAQ />
    </DarkBackground>
  );
};
